import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import CircularProgress from '@mui/material/CircularProgress'
import InfiniteScroll from 'react-infinite-scroller'
import FlagCircleIcon from '@mui/icons-material/FlagCircle'
import { format } from 'date-fns'
import TextField from '@/v2/components/common/TextField'
import Select from '@/v2/components/common/Select'
import Typography from '@/v2/components/common/Typography'
import PatientAvatar from '@/v2/components/common/PatientAvatar'
import MessageStatus from '@/v2/containers/messages/MessageStatus'
import RTConversations from '@/v2/hooks/subscriptions/messages/RTConversations'
import {
  useOpenConversations,
  useClosedConversations,
} from '@/v2/hooks/messages/useConversations'
import { sanitizeConversationSearch } from '@/v2/utils/searches'
import { getDateTimeWithoutTimezone } from '@/v2/utils/convert'
import { formatPhone } from '@/v2/utils/format'
import { DAY_IN_US_FORMAT } from '@/v2/constants'
import { filterConversations } from '../ConversationsList/OpenClosedConversations/utils'
import SearchResults from '../ConversationsList/SearchResults/index'
import {
  ConversationsWrapper,
  SearchWrapper,
  TabsWrapper,
  ConversationsList,
  ConversationItem,
  PatientInfo,
  LastMessageText,
  MessageBadge,
  EmptyState,
} from './styles'

const closedOptions = [
  { value: 'all', label: 'All' },
  { value: 'unanswered', label: 'Unanswered' },
  { value: 'flagged', label: 'Flagged' },
]

const allOptions = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'unanswered', label: 'Unanswered' },
  { value: 'flagged', label: 'Flagged' },
]

const MobileConversationItem = ({
  id,
  flagged,
  read,
  patients,
  phoneNumber,
  lastMessage,
  unreadMessagesCount,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const patientsArray = Array.isArray(patients)
    ? patients
    : [patients].filter(Boolean)
  const firstPatient = patientsArray[0]
  const names = patientsArray
    .map(p => p.fullName || formatPhone(p.phoneNumber))
    .join(', ')

  return (
    <ConversationItem
      theme={theme.palette}
      onClick={() => navigate(`/v2/messages/${id}`)}
    >
      <div style={{ position: 'relative' }}>
        <PatientAvatar
          patient={firstPatient}
          names={names}
          size={50}
          sx={{ width: 50, height: 50 }}
        />
        {patientsArray.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              right: -4,
              background: theme.palette.primary.main,
              color: '#000',
              borderRadius: '10px',
              padding: '0 6px',
              fontSize: '11px',
              fontWeight: 500,
              minWidth: 18,
              textAlign: 'center',
              lineHeight: '18px',
            }}
          >
            +{patientsArray.length - 1}
          </div>
        )}
      </div>
      <PatientInfo>
        <Typography fontSize="14px" fontWeight="500" lineHeight="20px">
          {names || formatPhone(phoneNumber)}
        </Typography>
        <LastMessageText>
          <Typography
            fontSize="13px"
            variant="h4"
            lineHeight="18px"
            className="last-message"
            color={theme.palette.gray?.main || '#666'}
          >
            <MessageStatus
              type={lastMessage?.type}
              status={lastMessage?.deliveryStatus}
            />
            {lastMessage?.body || 'No message found'}
          </Typography>
        </LastMessageText>
        <Typography
          fontSize="12px"
          variant="h4"
          lineHeight="16px"
          color={theme.palette.gray?.main || '#999'}
        >
          {lastMessage?.createdAt
            ? format(
                getDateTimeWithoutTimezone(lastMessage.createdAt),
                DAY_IN_US_FORMAT
              )
            : ''}
        </Typography>
      </PatientInfo>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
      >
        {flagged && <FlagCircleIcon sx={{ fontSize: 20, color: '#f44336' }} />}
        {(!read || unreadMessagesCount > 0) && (
          <MessageBadge theme={theme.palette}>
            {unreadMessagesCount || ''}
          </MessageBadge>
        )}
      </div>
    </ConversationItem>
  )
}

const OpenConversationsContent = ({ filter }) => {
  const navigate = useNavigate()
  const {
    conversations,
    isError,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useOpenConversations()

  useEffect(() => {
    if (isError) navigate('/500')
  }, [isError, navigate])

  const filteredConversations = useMemo(
    () => filterConversations([...conversations], filter),
    [conversations, filter]
  )

  return (
    <InfiniteScroll
      element="div"
      useWindow={false}
      initialLoad={filter !== 'all'}
      loadMore={() => !isLoading && hasNextPage && fetchNextPage()}
      hasMore={hasNextPage}
      loader={
        <CircularProgress
          key="loader"
          sx={{ margin: '16px auto', display: 'block' }}
        />
      }
    >
      {filteredConversations.map(conversation => (
        <MobileConversationItem key={conversation.id} {...conversation} />
      ))}
      {filteredConversations.length === 0 && !isLoading && (
        <EmptyState>
          <Typography>No conversations</Typography>
        </EmptyState>
      )}
      {isLoading && (
        <CircularProgress sx={{ margin: '16px auto', display: 'block' }} />
      )}
    </InfiniteScroll>
  )
}

const ClosedConversationsContent = ({ filter }) => {
  const navigate = useNavigate()
  const {
    conversations,
    isError,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useClosedConversations()

  useEffect(() => {
    if (isError) navigate('/500')
  }, [isError, navigate])

  const filteredConversations = useMemo(
    () => filterConversations([...conversations], filter),
    [conversations, filter]
  )

  return (
    <InfiniteScroll
      element="div"
      useWindow={false}
      initialLoad={filter !== 'all'}
      loadMore={() => !isLoading && hasNextPage && fetchNextPage()}
      hasMore={hasNextPage}
      loader={
        <CircularProgress
          key="loader"
          sx={{ margin: '16px auto', display: 'block' }}
        />
      }
    >
      {filteredConversations.map(conversation => (
        <MobileConversationItem key={conversation.id} {...conversation} />
      ))}
      {filteredConversations.length === 0 && !isLoading && (
        <EmptyState>
          <Typography>No conversations</Typography>
        </EmptyState>
      )}
      {isLoading && (
        <CircularProgress sx={{ margin: '16px auto', display: 'block' }} />
      )}
    </InfiniteScroll>
  )
}

const MobileConversationsList = () => {
  const theme = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const [tab, setTab] = useState(0)
  const [filter, setFilter] = useState('all')

  const debounceRef = useRef(null)
  const handleSearch = value => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchTerm(sanitizeConversationSearch(value))
    }, 500)
  }

  return (
    <ConversationsWrapper>
      <RTConversations />
      <SearchWrapper>
        <TextField
          name="search"
          placeholder="Find or start a conversation"
          endAdornment={
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          }
          sx={{ width: '100%' }}
          onChange={value => handleSearch(value)}
        />
      </SearchWrapper>

      {!searchTerm && (
        <>
          <TabsWrapper theme={theme.palette}>
            <Tabs value={tab} sx={{ minHeight: 40 }}>
              <Tab
                label="Open"
                onClick={() => setTab(0)}
                sx={{ minHeight: 40, py: 1 }}
              />
              <Tab
                label="Closed"
                onClick={() => setTab(1)}
                sx={{ minHeight: 40, py: 1 }}
              />
            </Tabs>
            <Select
              variant="standard"
              defaultValue="all"
              options={tab === 0 ? allOptions : closedOptions}
              onChange={value => setFilter(value)}
              sx={{ minWidth: 100 }}
            />
          </TabsWrapper>

          <ConversationsList>
            {tab === 0 ? (
              <OpenConversationsContent filter={filter} />
            ) : (
              <ClosedConversationsContent filter={filter} />
            )}
          </ConversationsList>
        </>
      )}

      {!!searchTerm && (
        <ConversationsList>
          <SearchResults searchTerm={searchTerm} />
        </ConversationsList>
      )}
    </ConversationsWrapper>
  )
}

export default MobileConversationsList
